import {
  getDate,
  parseISO,
  formatISO,
  eachDayOfInterval,
  isBefore,
  isAfter,
  setHours,
  startOfDay,
  endOfDay,
  isWithinInterval,
} from "date-fns";
import React, { useCallback, useEffect, useState } from "react";
import Calendar, { DateElement } from "./Calendar";
import styled from "@emotion/styled";
import * as uuid from "uuid";

const OccupancyCalendarContainer = styled.div`
  .calendarLoading {
    height: 36em;
    background-image: url(data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0idXRmLTgiPz4KPHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHN0eWxlPSJiYWNrZ3JvdW5kOiByZ2IoMjQxLCAyNDIsIDI0MykiIHdpZHRoPSI0MyIgaGVpZ2h0PSI0MyIgcHJlc2VydmVBc3BlY3RSYXRpbz0ieE1pZFlNaWQiIHZpZXdCb3g9IjAgMCA0MyA0MyI+PGcgdHJhbnNmb3JtPSJzY2FsZSgwLjE3KSI+PGRlZnM+PGcgaWQ9InN0cmlwZSI+PHBhdGggZD0iTTI1NiAtMTI4IEwzODQgLTEyOCBMLTEyOCAzODQgTC0xMjggMjU2IFoiIGZpbGw9IiNmZmZmZmYiPjwvcGF0aD48cGF0aCBkPSJNMzg0IDAgTDM4NCAxMjggTDEyOCAzODQgTDAgMzg0IFoiIGZpbGw9IiNmZmZmZmYiPjwvcGF0aD48L2c+PC9kZWZzPjxnIHRyYW5zZm9ybT0idHJhbnNsYXRlKDE5Ni4yMDggMCkiPjx1c2UgaHJlZj0iI3N0cmlwZSIgeD0iLTI1NiIgeT0iMCI+PC91c2U+PHVzZSBocmVmPSIjc3RyaXBlIiB4PSIwIiB5PSIwIj48L3VzZT48YW5pbWF0ZVRyYW5zZm9ybSBhdHRyaWJ1dGVOYW1lPSJ0cmFuc2Zvcm0iIHR5cGU9InRyYW5zbGF0ZSIga2V5VGltZXM9IjA7MSIgcmVwZWF0Q291bnQ9ImluZGVmaW5pdGUiIGR1cj0iMC41cyIgdmFsdWVzPSIwIDA7IDI1NiAwIj48L2FuaW1hdGVUcmFuc2Zvcm0+PC9nPjwvZz48L3N2Zz4K);
  }

  .calendarLegend {
    display: grid;
    grid-template-columns: 1.8rem auto 1.8rem auto 1.8rem auto;
    gap: 0.8rem;
    font-size: 0.8rem;
    align-items: center;
  }

  .calendarLegend dd {
    margin: 0;
  }

  .calendarDay > * {
    background-color: white;
    border: 1px solid white;
    cursor: pointer;
    text-align: center;
    display: block;
    font-size: 0.9rem;
    transition: opacity 0.1s ease-in-out;
    opacity: 1;
    aspect-ratio: 1 / 1;
    width: 100%;

    &:hover {
      opacity: 0.8;
    }

    &.disabled,
    &[disabled] {
      cursor: default;
      opacity: 0.2;
    }
  }
`;

class Occupancy {
  constructor(
    public id: string,
    public start: Date,
    public end: Date,
    public occupancyType: string,
    public title: string,
  ) {}

  static fromJson(json: any): Occupancy {
    const start = parseISO(json.start_date);
    const end = parseISO(json.end_date);

    return new Occupancy(json.id || uuid.v4(), start, end, json.reservation_type, json.to_s);
  }
}

type OccupiedDates = {
  [key: string]: Set<Occupancy>;
};

function formatIsoDate(date) {
  return formatISO(date, { representation: "date" });
}

function mapOccupiedDates(occupancies: Occupancy[]) {
  const occupiedDates: OccupiedDates = {};

  for (const occupancy of occupancies) {
    try {
      eachDayOfInterval({ start: occupancy.start, end: occupancy.end }).forEach((occupiedDate) => {
        const occupiedDateString = formatIsoDate(occupiedDate);
        occupiedDates[occupiedDateString] = occupiedDates[occupiedDateString]?.add(occupancy) || new Set([occupancy]);
      });
    } catch {}
  }

  return occupiedDates;
}

class OccupancyWindow {
  public occupiedDates: OccupiedDates;

  constructor(
    public windowFrom: Date,
    public windowTo: Date,
    public occupancies: Occupancy[],
  ) {
    this.occupiedDates = mapOccupiedDates(occupancies);
  }

  static fromJson(json: any): OccupancyWindow {
    return new OccupancyWindow(
      parseISO(json.window_from),
      parseISO(json.window_to),
      Array.from(json.occupancies).map((occupancyJson) => Occupancy.fromJson(occupancyJson)),
    );
  }
}

interface OccupancyCalendarDayProps {
  dateString: string;
  occupancyWindow: OccupancyWindow;
  templateUrl: string;
}

const occupancyDateClassNames = (date: Date, occupancies: Set<Occupancy>): string[] => {
  const midDay = setHours(startOfDay(date), 12);

  return Array.from(occupancies).map(({ start, end, occupancyType }) => {
    if (
      isWithinInterval(end, {
        start: startOfDay(date),
        end: midDay,
      })
    ) {
      return `${occupancyType}-forenoon`;
    }
    if (
      isWithinInterval(start, {
        start: midDay,
        end: endOfDay(date),
      })
    ) {
      return `${occupancyType}-afternoon`;
    }
    return `${occupancyType}-fullday`;
  });
};

const i18n = {
  translations: {
    de: {
      fetchError: "Belegungsdaten konnten nicht geladen werden.",
      unreliableOccupancyData: "Die Belegungsdaten sind unvollständig",
      "provisionally_reserved-afternoon": "Nachmittags provisorisch reserviert",
      "definitely_reserved-afternoon": "Nachmittags definitiv reserviert",
      "closed-afternoon": "Nachmittags geschlossen",
      "provisionally_reserved-fullday": "Ganztags provisorisch reserviert",
      "definitely_reserved-fullday": "Ganztags definitiv reserviert",
      "closed-fullday": "Ganztags geschlossen",
      "provisionally_reserved-forenoon": "Vormittags provisorisch reserviert",
      "definitely_reserved-forenoon": "Vormittags definitiv reserviert",
      "closed-forenoon": "Vormittags geschlossen",
    },
    fr: {
      fetchError: "Erreur lors du chargement des occupations.",
      unreliableOccupancyData: "Les données d'occupation sont incomplètes",
      "provisionally_reserved-afternoon": "Réservé provisoirement l’après-midi",
      "definitely_reserved-afternoon": "Réservé définitivement l’après-midi",
      "closed-afternoon": "Fermé l’après-midi",
      "provisionally_reserved-fullday": "Réservé provisoirement toute la journée",
      "definitely_reserved-fullday": "Réservé définitivement toute la journée",
      "closed-fullday": "Fermé toute la journée",
      "provisionally_reserved-forenoon": "Réservé provisoirement le matin",
      "definitely_reserved-forenoon": "Réservé définitivement le matin",
      "closed-forenoon": "Fermé le matin",
    },
    it: {
      fetchError: "Errore nel caricamento delle assegnazioni",
      unreliableOccupancyData: "I dati sull'occupazione sono incompleti",
      "provisionally_reserved-afternoon": "Riservato provvisoriamente nel pomeriggio",
      "definitely_reserved-afternoon": "Riservato definitivamente nel pomeriggio",
      "closed-afternoon": "Chiuso nel pomeriggio",
      "provisionally_reserved-fullday": "Riservato provvisoriamente per l’intera giornata",
      "definitely_reserved-fullday": "Riservato definitivamente per l’intera giornata",
      "closed-fullday": "Chiuso per l’intera giornata",
      "provisionally_reserved-forenoon": "Riservato provvisoriamente al mattino",
      "definitely_reserved-forenoon": "Riservato definitivamente al mattino",
      "closed-forenoon": "Chiuso al mattino",
    },
    en: {
      fetchError: "Occupancy data could not be retreived",
      unreliableOccupancyData: "Occupancy data is incomplete",
      "provisionally_reserved-afternoon": "Provisionally reserved in the afternoon",
      "definitely_reserved-afternoon": "Definitely reserved in the afternoon",
      "closed-afternoon": "Closed in the afternoon",
      "provisionally_reserved-fullday": "Provisionally reserved for the whole day",
      "definitely_reserved-fullday": "Definitely reserved for the whole day",
      "closed-fullday": "Closed for the whole day",
      "provisionally_reserved-forenoon": "Provisionally reserved in the morning",
      "definitely_reserved-forenoon": "Definitely reserved in the morning",
      "closed-forenoon": "Closed in the morning",
    },
  },
  t(key, locale = document.documentElement.lang) {
    return this.translations[locale][key];
  },
};

function OccupancyCalendarDay({ dateString, occupancyWindow, templateUrl }: OccupancyCalendarDayProps) {
  const date = parseISO(dateString);
  const occupancies = occupancyWindow?.occupiedDates[dateString];
  const disabled =
    !occupancyWindow || isBefore(date, occupancyWindow.windowFrom) || isAfter(date, occupancyWindow.windowTo);
  const classNames = occupancies && occupancyDateClassNames(date, occupancies).join(" ");
  const handleClick = () => (window.top.location.href = templateUrl.replace("__DATE__", dateString));
  const title =
    occupancies &&
    Array.from(occupancies)
      .map((occupancy) => occupancy.title)
      .join("; ");

  return (
    <button type="button" onClick={handleClick} disabled={disabled} className={classNames} title={title}>
      {getDate(date)}
    </button>
  );
}

function OccupancyCalendarLegend() {
  const items = [
    "provisionally_reserved-afternoon",
    "definitely_reserved-afternoon",
    "closed-afternoon",
    "provisionally_reserved-fullday",
    "definitely_reserved-fullday",
    "closed-fullday",
    "provisionally_reserved-forenoon",
    "definitely_reserved-forenoon",
    "closed-forenoon",
  ];

  return (
    <>
      <h6>Legende</h6>
      <dl className="calendarLegend">
        {items.map((item) => (
          <>
            <dt className="calendarDay">
              <button type="button" className={item}>
                15
              </button>
            </dt>
            <dd>{i18n.t(item)}</dd>
          </>
        ))}
      </dl>
    </>
  );
}

export interface OccupancyCalendarProps {
  start?: string;
  monthsCount?: number;
  occupancyWindowUrl?: string;
  templateUrl: string;
}

function OccupancyCalendar({ start, monthsCount = 9, occupancyWindowUrl, templateUrl }: OccupancyCalendarProps) {
  const [occupancyWindow, setOccupancyWindow] = useState(undefined);
  const [fetchError, setFetchError] = useState(undefined);
  const dateElement: DateElement = useCallback(
    (dateString: string) => (
      <OccupancyCalendarDay
        occupancyWindow={occupancyWindow}
        dateString={dateString}
        templateUrl={templateUrl}
      ></OccupancyCalendarDay>
    ),
    [templateUrl, occupancyWindow],
  );

  useEffect(() => {
    (async () => {
      const result = await fetch(occupancyWindowUrl);

      if (result.status == 200) {
        setOccupancyWindow(OccupancyWindow.fromJson(await result.json()));
        setFetchError(false);
      } else {
        setFetchError(i18n.t("fetchError"));
      }
    })();
  }, [occupancyWindowUrl]);

  return (
    <OccupancyCalendarContainer className="occupancyCalendar">
      {fetchError && <div className="alert alert-danger">{fetchError}</div>}
      {occupancyWindow && occupancyWindow.occupancies.length == 0 && (
        <div className="alert alert-warning">{i18n.t("unreliableOccupancyData")}</div>
      )}
      <Calendar start={start} monthsCount={monthsCount} dateElement={dateElement}></Calendar>
      <OccupancyCalendarLegend></OccupancyCalendarLegend>
    </OccupancyCalendarContainer>
  );
}

export default OccupancyCalendar;
